import { create } from 'zustand';
import { customerApi } from '../../../api/api';
import type { 
  CustomerDto, 
  AppCreateCustomerRequest, 
  AppUpdateCustomerRequest, 
  CustomerDetailResponse,
  CustomerNoteResponse,
  CreateCustomerNoteRequest,
  UpdateCustomerNoteRequest,
} from '../../../api/generated';

type CustomerState = {
  customers: CustomerDto[];
  customerDetail: CustomerDetailResponse | null;
  customerNotes: CustomerNoteResponse[];
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
  } | null;
  loading: boolean;
  error: string | null;

  // Actions
  loadCustomers: (filters?: { query?: string; page?: number; size?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => Promise<void>;
  loadCustomerDetail: (customerId: string) => Promise<void>;
  createCustomer: (data: AppCreateCustomerRequest) => Promise<void>;
  updateCustomer: (customerId: string, data: AppUpdateCustomerRequest) => Promise<void>;
  deleteCustomer: (customerId: string) => Promise<void>;
  
  // Notes actions
  loadCustomerNotes: (customerId: string) => Promise<void>;
  createCustomerNote: (customerId: string, data: CreateCustomerNoteRequest) => Promise<CustomerNoteResponse>;
  updateCustomerNote: (customerId: string, noteId: string, data: UpdateCustomerNoteRequest) => Promise<void>;
  deleteCustomerNote: (customerId: string, noteId: string) => Promise<void>;
  uploadNoteAttachment: (customerId: string, noteId: string, file: File) => Promise<void>;
  deleteNoteAttachment: (customerId: string, noteId: string, attachmentId: string) => Promise<void>;
  
  clearError: () => void;
};

export const useCustomerStore = create<CustomerState>(set => ({
  customers: [],
  customerDetail: null,
  customerNotes: [],
  pagination: null,
  loading: false,
  error: null,

  loadCustomers: async (filters = {}) => {
    set({ loading: true, error: null });

    try {
      const response = await customerApi.getCustomers({
        query: filters.query,
        page: filters.page || 1,
        size: filters.size || 20,
        sortBy: filters.sortBy || 'firstName',
        sortOrder: filters.sortOrder || 'asc',
      });

      const data = response.data;
      set({
        customers: data.customers || [],
        pagination: data.pagination
          ? {
              page: data.pagination.page || 1,
              size: data.pagination.limit || 20,
              totalElements: data.pagination.total || 0,
              totalPages: data.pagination.totalPages || 0,
            }
          : null,
        loading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při načítání zákazníků',
        loading: false,
      });
    }
  },

  loadCustomerDetail: async (customerId: string) => {
    set({ loading: true, error: null });

    try {
      const response = await customerApi.getCustomerDetail({ customerId });
      set({
        customerDetail: response.data,
        loading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při načítání detailu zákazníka',
        loading: false,
      });
    }
  },

  createCustomer: async (data: AppCreateCustomerRequest) => {
    set({ loading: true, error: null });

    try {
      await customerApi.createCustomer({ appCreateCustomerRequest: data });
      set({ loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při vytváření zákazníka',
        loading: false,
      });
      throw error;
    }
  },

  updateCustomer: async (customerId: string, data: AppUpdateCustomerRequest) => {
    set({ loading: true, error: null });

    try {
      await customerApi.updateCustomer({ customerId, appUpdateCustomerRequest: data });
      set({ loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při aktualizaci zákazníka',
        loading: false,
      });
      throw error;
    }
  },

  deleteCustomer: async (customerId: string) => {
    set({ loading: true, error: null });

    try {
      await customerApi.deleteCustomer({ customerId });
      set({ loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při mazání zákazníka',
        loading: false,
      });
      throw error;
    }
  },

  // Notes actions
  loadCustomerNotes: async (customerId: string) => {
    set({ loading: true, error: null });

    try {
      const response = await customerApi.getCustomerNotes({ customerId });
      set({
        customerNotes: response.data.notes || [],
        loading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při načítání poznámek',
        loading: false,
      });
    }
  },

  createCustomerNote: async (customerId: string, data: CreateCustomerNoteRequest) => {
    set({ loading: true, error: null });

    try {
      const response = await customerApi.createCustomerNote({ customerId, createCustomerNoteRequest: data });
      set({ loading: false });
      return response.data;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při vytváření poznámky',
        loading: false,
      });
      throw error;
    }
  },

  updateCustomerNote: async (customerId: string, noteId: string, data: UpdateCustomerNoteRequest) => {
    set({ loading: true, error: null });

    try {
      await customerApi.updateCustomerNote({ customerId, noteId, updateCustomerNoteRequest: data });
      set({ loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při aktualizaci poznámky',
        loading: false,
      });
      throw error;
    }
  },

  deleteCustomerNote: async (customerId: string, noteId: string) => {
    set({ loading: true, error: null });

    try {
      await customerApi.deleteCustomerNote({ customerId, noteId });
      set({ loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při mazání poznámky',
        loading: false,
      });
      throw error;
    }
  },

  uploadNoteAttachment: async (customerId: string, noteId: string, file: File) => {
    set({ loading: true, error: null });

    try {
      await customerApi.uploadAttachment({ customerId, noteId, file });
      set({ loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při nahrávání přílohy',
        loading: false,
      });
      throw error;
    }
  },

  deleteNoteAttachment: async (customerId: string, noteId: string, attachmentId: string) => {
    set({ loading: true, error: null });

    try {
      await customerApi.deleteAttachment({ customerId, noteId, attachmentId });
      set({ loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při mazání přílohy',
        loading: false,
      });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
